import {
  addDaysToDate,
  formatDate,
  getDateDifference,
  getStartOfDay,
  parseDate as parseDateString,
} from '@travelata-libs/utils';
import type { DateRange } from '@/shared/model';

export const FLEXIBLE_OPTIONS = [1, 2, 3] as const;
export const MIN_FLEXIBLE = 0;
export const MAX_FLEXIBLE = 3;
export const MAX_RANGE_DAYS = 30;
export const ISO_FORMAT = 'yyyy-MM-dd';

// utils.getStartOfDay возвращает timestamp (number) — приводим к Date для сравнений/возврата.
function startOfDay(d: Date): Date {
  return new Date(getStartOfDay(d));
}

export function today(): Date {
  return startOfDay(new Date());
}

export function getMinDate(): Date {
  return addDaysToDate(today(), 1);
}

export function getMaxDate(): Date {
  return addDaysToDate(today(), 365);
}

export function toISO(d: Date): string {
  return formatDate(d, ISO_FORMAT);
}

export function parseDate(s: string): Date {
  const parsed = parseDateString(s);
  if (!parsed) {
    return getMinDate();
  }
  return startOfDay(parsed);
}

export function clampDate(d: Date, min: Date = getMinDate(), max: Date = getMaxDate()): Date {
  const day = startOfDay(d);
  if (day < min) {
    return startOfDay(min);
  }
  if (day > max) {
    return startOfDay(max);
  }
  return day;
}

export function clampFlexible(n: number): number {
  const rounded = Math.round(Number(n));
  if (Number.isNaN(rounded)) {
    return MIN_FLEXIBLE;
  }
  return Math.min(MAX_FLEXIBLE, Math.max(MIN_FLEXIBLE, rounded));
}

export function defaultDateFrom(): DateRange {
  const iso = toISO(getMinDate());
  return { from: iso, to: iso, daysFlexible: 0 };
}

export function deriveDateFrom(anchor: Date, flexible: number): DateRange {
  const a = clampDate(anchor);
  const f = clampFlexible(flexible);

  if (f === 0) {
    const iso = toISO(a);
    return { from: iso, to: iso, daysFlexible: 0 };
  }

  const from = clampDate(addDaysToDate(a, -f));
  let to = clampDate(addDaysToDate(a, f));
  if (getDateDifference(to, from, 'days') > MAX_RANGE_DAYS) {
    to = clampDate(addDaysToDate(from, MAX_RANGE_DAYS));
  }

  const fromIso = toISO(from);
  const toIso = toISO(to);
  const daysFlexible = fromIso === toIso ? 0 : f;
  return { from: fromIso, to: toIso, daysFlexible };
}

export function getAnchor(value: DateRange): Date {
  const from = parseDate(value.from);
  const to = parseDate(value.to);
  const min = getMinDate();

  if (value.daysFlexible > 0) {
    return clampDate(addDaysToDate(to, -value.daysFlexible), min);
  }

  const half = Math.floor(getDateDifference(to, from, 'days') / 2);
  return clampDate(addDaysToDate(from, half), min);
}

export function correctDateRange(range: DateRange): DateRange | null {
  const min = getMinDate();
  const to = parseDate(range.to);
  if (to < min) {
    return null;
  }
  if (parseDate(range.from) >= min) {
    return range;
  }

  const from = toISO(min);
  const daysFlexible = from === toISO(to) ? 0 : range.daysFlexible;
  return { from, to: range.to, daysFlexible };
}

export function normalizeDateFrom(value?: DateRange | null): DateRange {
  if (!value) {
    return defaultDateFrom();
  }

  const min = getMinDate();
  const max = getMaxDate();

  let fromDate = parseDate(value.from);
  let toDate = parseDate(value.to);

  if (fromDate > toDate) {
    const tmp = fromDate;
    fromDate = toDate;
    toDate = tmp;
  }

  if (fromDate < min && toDate < min) {
    return defaultDateFrom();
  }

  const from = clampDate(fromDate, min, max);
  let to = clampDate(toDate, min, max);

  if (getDateDifference(to, from, 'days') > MAX_RANGE_DAYS) {
    to = clampDate(addDaysToDate(from, MAX_RANGE_DAYS), min, max);
  }

  const fromIso = toISO(from);
  const toIso = toISO(to);
  const daysFlexible = fromIso === toIso ? 0 : clampFlexible(value.daysFlexible);
  return { from: fromIso, to: toIso, daysFlexible };
}
